/**
 * TunnelStatusGrid
 *
 * Grid of TunnelStatusCards showing VPN health per site.
 */

import { useNavigate } from 'react-router-dom';
import { useTunnels } from '../../hooks/useSecurity';
import { TunnelStatusCard } from './TunnelStatusCard';
import { Shield, Loader2 } from 'lucide-react';

interface TunnelStatusGridProps {
  onSiteClick?: (siteId: string) => void;
}

export function TunnelStatusGrid({ onSiteClick }: TunnelStatusGridProps) {
  const navigate = useNavigate();
  const { data, isLoading } = useTunnels();

  if (isLoading) {
    return (
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-ems-text-muted" />
          <h2 className="text-sm font-semibold text-ems-text-primary uppercase tracking-wider">VPN Tunnel Status</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-ems-text-muted" />
        </div>
      </div>
    );
  }

  const tunnels = data?.tunnels || [];

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-ems-text-muted" />
        <h2 className="text-sm font-semibold text-ems-text-primary uppercase tracking-wider">VPN Tunnel Status</h2>
        <span className="text-xs text-ems-text-muted">({tunnels.length} tunnels)</span>
      </div>

      {tunnels.length === 0 ? (
        <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-8 text-center">
          <Shield className="w-8 h-8 text-ems-text-muted/30 mx-auto mb-2" />
          <p className="text-sm text-ems-text-muted">No VPN tunnels configured</p>
          <p className="text-xs text-ems-text-muted mt-1">Tunnel data will appear when VPN monitoring is enabled</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {tunnels.map((tunnel) => (
            <TunnelStatusCard
              key={tunnel.site_id}
              tunnel={tunnel}
              onClick={(siteId) => {
                onSiteClick?.(siteId);
                navigate(`/security/site/${siteId}`);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
